import { z } from "zod";
import { recipeContentV1Schema } from "./studio.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const recipeProductionStageV1Schema = z.enum([
    "SOURCE_LOCK",
    "RECIPE_NORMALIZATION",
    "FIDELITY_AND_SAFETY_REVIEW",
    "EDITORIAL_COPY_APPROVAL",
    "DESIGN_SYSTEM",
    "SHOT_PLAN",
    "REPRESENTATIVE_RECIPE_PROOF",
    "DESIGN_APPROVAL",
    "FOOD_ASSETS",
    "VISUAL_FIDELITY_EVALUATION",
    "TARGETED_REPAIR",
    "DETERMINISTIC_LAYOUT",
    "DIGITAL_RENDER",
    "FINAL_PROOF",
]);
const recipeEvidenceStatusSchema = z.enum(["SOURCE_EXPLICIT", "HUMAN_CONFIRMED"]);
export const recipeFieldEvidenceV1Schema = z
    .object({
    sourceAssetId: z.string().min(1).max(160),
    sourceHashSha256: sha256Schema,
    sourceQuote: z.string().trim().min(1).max(4_000),
    status: recipeEvidenceStatusSchema,
})
    .strict();
export const recipeProductionIngredientV1Schema = z
    .object({
    id: z.string().min(1).max(100),
    name: z.string().trim().min(1).max(200),
    quantity: z.string().trim().min(1).max(80),
    unit: z.string().trim().max(80),
    preparation: z.string().trim().max(300),
    evidence: recipeFieldEvidenceV1Schema,
})
    .strict();
export const recipeProductionInstructionV1Schema = z
    .object({
    id: z.string().min(1).max(100),
    text: z.string().trim().min(1).max(2_000),
    usedIngredientIds: z.array(z.string().min(1).max(100)).max(40),
    evidence: recipeFieldEvidenceV1Schema,
})
    .strict();
export const recipeSafetyReviewV1Schema = z
    .object({
    reviewed: z.literal(true),
    allergens: z.array(z.string().trim().min(1).max(100)).min(1).max(20),
    storage: z.string().trim().min(1).max(1_000),
    foodSafetyNotes: z.array(z.string().trim().min(1).max(1_000)).max(20),
    preservationMethod: z.enum(["NONE", "REFRIGERATED", "TESTED_CANNING"]),
    evidence: z.array(recipeFieldEvidenceV1Schema).min(1).max(20),
})
    .strict();
export const recipeProductionRecipeV1Schema = z
    .object({
    id: z.string().min(1).max(100),
    title: z.string().trim().min(1).max(200),
    familyStory: z.string().trim().min(1).max(4_000),
    yield: z.string().trim().min(1).max(120),
    preparationMinutes: z.number().int().nonnegative().max(1_440),
    cookingMinutes: z.number().int().nonnegative().max(2_880),
    sourceAssetId: z.string().min(1).max(160),
    sourceText: z.string().trim().min(1).max(30_000),
    sourceHashSha256: sha256Schema,
    ingredients: z.array(recipeProductionIngredientV1Schema).min(1).max(100),
    instructions: z.array(recipeProductionInstructionV1Schema).min(1).max(100),
    notes: z.array(z.string().trim().min(1).max(1_000)).max(20),
    attribution: z.string().trim().min(1).max(500),
    safety: recipeSafetyReviewV1Schema,
})
    .strict();
export const recipeProductionCollectionV1Schema = z
    .object({
    version: z.literal(1),
    fixtureId: z.string().min(1).max(160),
    synthetic: z.boolean(),
    title: z.string().trim().min(1).max(200),
    subtitle: z.string().trim().min(1).max(300),
    sourceSnapshotHashSha256: sha256Schema,
    recipes: z.array(recipeProductionRecipeV1Schema).min(1).max(100),
})
    .strict();
export const recipeClaimAuthorityV1Schema = z.enum([
    "CUSTOMER_SOURCE",
    "AUTHORITATIVE_REFERENCE",
    "HUMAN_CONFIRMED",
]);
export const recipeClaimEvidenceV2Schema = z
    .object({
    id: z.string().min(1).max(160),
    fieldPath: z.string().min(1).max(500),
    authority: recipeClaimAuthorityV1Schema,
    sourceAssetId: z.string().min(1).max(160),
    sourceHashSha256: sha256Schema,
    sourceQuote: z.string().trim().min(1).max(4_000),
    referenceUri: z.string().url().max(2_000).optional(),
    reviewedBy: z.string().min(1).max(160).optional(),
})
    .strict()
    .superRefine((evidence, context) => {
    if (evidence.authority === "AUTHORITATIVE_REFERENCE" && !evidence.referenceUri)
        context.addIssue({
            code: "custom",
            path: ["referenceUri"],
            message: "Authoritative reference evidence requires an exact source URI.",
        });
    if (evidence.authority === "HUMAN_CONFIRMED" && !evidence.reviewedBy)
        context.addIssue({
            code: "custom",
            path: ["reviewedBy"],
            message: "Human-confirmed evidence requires reviewer identity.",
        });
});
export const recipeAllergenV2Schema = z
    .object({
    allergen: z.enum([
        "MILK",
        "EGG",
        "FISH",
        "CRUSTACEAN_SHELLFISH",
        "TREE_NUT",
        "PEANUT",
        "WHEAT",
        "SOY",
        "SESAME",
        "OTHER",
    ]),
    otherName: z.string().trim().min(1).max(100).optional(),
    status: z.enum(["CONTAINS", "DOES_NOT_CONTAIN", "UNKNOWN"]),
    evidenceIds: z.array(z.string().min(1).max(160)).min(1).max(20),
})
    .strict()
    .superRefine((claim, context) => {
    if (claim.allergen === "OTHER" && !claim.otherName)
        context.addIssue({
            code: "custom",
            path: ["otherName"],
            message: "Other allergen claims require the specific substance name.",
        });
});
export const recipeSafetyV2Schema = z
    .object({
    reviewed: z.literal(true),
    storage: z.string().trim().min(1).max(2_000),
    temperature: z.string().trim().max(1_000),
    doneness: z.string().trim().max(1_000),
    preservationMethod: z.enum(["NONE", "REFRIGERATED", "FROZEN", "TESTED_CANNING"]),
    preservationScope: z.string().trim().min(1).max(2_000),
    allergens: z.array(recipeAllergenV2Schema).min(9).max(40),
    evidenceIds: z.array(z.string().min(1).max(160)).min(1).max(100),
})
    .strict()
    .superRefine((safety, context) => {
    const major = new Set(safety.allergens
        .filter(({ allergen }) => allergen !== "OTHER")
        .map(({ allergen }) => allergen));
    if (major.size !== 9)
        context.addIssue({
            code: "custom",
            path: ["allergens"],
            message: "All nine major US allergens require an explicit contains, absent, or unknown review.",
        });
});
export const recipeProductionRecipeV2Schema = z
    .object({
    version: z.literal(2),
    content: recipeContentV1Schema,
    sourceSnapshotHashSha256: sha256Schema,
    evidence: z.array(recipeClaimEvidenceV2Schema).min(1).max(1_000),
    ingredientMethodLinks: z
        .array(z
        .object({
        ingredientId: z.string().min(1).max(80),
        instructionIds: z.array(z.string().min(1).max(80)).min(1).max(100),
    })
        .strict())
        .max(2_000),
    equipment: z.array(z.string().trim().min(1).max(200)).max(100),
    safety: recipeSafetyV2Schema,
    visualAssets: z
        .array(z
        .object({
        assetId: z.string().min(1).max(160),
        role: z.enum(["HERO", "PROCESS", "SOURCE_CARD", "PORTRAIT", "INGREDIENT_STILL_LIFE"]),
        stepId: z.string().min(1).max(80).optional(),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
        checksumSha256: sha256Schema,
    })
        .strict())
        .max(200),
})
    .strict()
    .superRefine((recipe, context) => {
    const ingredientIds = new Set(recipe.content.ingredientGroups.flatMap(({ ingredients }) => ingredients.map(({ id }) => id)));
    const instructionIds = new Set(recipe.content.instructions.map(({ id }) => id));
    if (ingredientIds.size === 0)
        context.addIssue({
            code: "custom",
            path: ["content", "ingredientGroups"],
            message: "Production recipes require at least one ingredient group.",
        });
    for (const [index, link] of recipe.ingredientMethodLinks.entries()) {
        if (!ingredientIds.has(link.ingredientId))
            context.addIssue({
                code: "custom",
                path: ["ingredientMethodLinks", index, "ingredientId"],
                message: "Ingredient-method links must reference a current ingredient.",
            });
        if (link.instructionIds.some((id) => !instructionIds.has(id)))
            context.addIssue({
                code: "custom",
                path: ["ingredientMethodLinks", index, "instructionIds"],
                message: "Ingredient-method links must reference current instructions.",
            });
    }
    const evidenceIds = new Set(recipe.evidence.map(({ id }) => id));
    for (const evidenceId of recipe.safety.evidenceIds)
        if (!evidenceIds.has(evidenceId))
            context.addIssue({
                code: "custom",
                path: ["safety", "evidenceIds"],
                message: "Safety claims must reference evidence included with this recipe.",
            });
    for (const [index, claim] of recipe.safety.allergens.entries())
        if (claim.evidenceIds.some((evidenceId) => !evidenceIds.has(evidenceId)))
            context.addIssue({
                code: "custom",
                path: ["safety", "allergens", index, "evidenceIds"],
                message: "Allergen decisions must reference evidence included with this recipe.",
            });
});
export const recipeLayoutIntentV1Schema = z
    .object({
    version: z.literal(1),
    recipeId: z.string().min(1).max(160),
    family: z.enum([
        "COMPACT",
        "STANDARD",
        "LONG_MULTI_COMPONENT",
        "PROCESS_LED",
        "STORY_LED",
        "PRESERVATION_CRITICAL",
        "MISSING_PHOTO",
    ]),
    opener: z.enum(["PHOTO_TOP", "PHOTO_LEFT", "INSET_EDITORIAL", "TYPOGRAPHIC"]),
    detail: z.enum(["BALANCED", "METHOD_FORWARD", "MULTI_PAGE", "PRESERVATION_FORWARD"]),
    estimatedDetailPages: z.number().int().positive().max(20),
    ingredientLineCount: z.number().int().nonnegative(),
    instructionLineCount: z.number().int().nonnegative(),
    storyLineCount: z.number().int().nonnegative(),
    assetRoles: z
        .array(z.enum(["HERO", "PROCESS", "SOURCE_CARD", "PORTRAIT", "INGREDIENT_STILL_LIFE"]))
        .max(200),
    rationaleCodes: z.array(z.string().min(1).max(100)).min(1).max(20),
})
    .strict();
export const cookbookArchitectureV1Schema = z
    .object({
    version: z.literal(1),
    title: z.string().trim().min(1).max(200),
    subtitle: z.string().trim().max(300),
    unitSystem: z.enum(["SOURCE_ONLY", "US", "METRIC", "DUAL_REVIEWED"]),
    chapters: z
        .array(z
        .object({
        id: z.string().min(1).max(120),
        title: z.string().trim().min(1).max(160),
        recipeIds: z.array(z.string().min(1).max(160)).min(1).max(200),
    })
        .strict())
        .min(1)
        .max(100),
    indexes: z
        .array(z.enum(["RECIPE", "INGREDIENT", "CONTRIBUTOR", "SEASON", "MENU"]))
        .min(1)
        .max(5),
    runningHeads: z.boolean(),
})
    .strict()
    .superRefine((architecture, context) => {
    const recipeIds = architecture.chapters.flatMap(({ recipeIds }) => recipeIds);
    if (new Set(recipeIds).size !== recipeIds.length)
        context.addIssue({
            code: "custom",
            path: ["chapters"],
            message: "A recipe may appear in only one primary cookbook chapter.",
        });
});
export const recipeProductionRecipeSchema = z.union([
    recipeProductionRecipeV1Schema,
    recipeProductionRecipeV2Schema,
]);
//# sourceMappingURL=recipe-production.js.map